import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Package,
  Boxes,
  ShoppingCart,
  Users,
  UserCog,
  FileText,
  Megaphone,
  DollarSign,
  Warehouse,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  Lock,
  UserPlus,
  Crown,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';
import { Attribution } from '../Attribution';

interface SidebarItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  permission?: string;
  children?: SidebarItem[];
  disabled?: boolean;
  comingSoon?: boolean;
}

const sidebarItems: SidebarItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/',
    permission: 'analytics.read',
  },
  {
    id: 'products',
    label: 'Products',
    icon: Package,
    path: '/products',
    permission: 'products.read',
  },
  {
    id: 'bundles',
    label: 'Bundles',
    icon: Boxes,
    path: '/bundles',
    permission: 'products.read',
  },
  {
    id: 'orders',
    label: 'Orders',
    icon: ShoppingCart,
    path: '/orders',
    permission: 'orders.read',
  },
  {
    id: 'customers',
    label: 'Customers',
    icon: Users,
    path: '/customers',
    permission: 'customers.read',
  },
  {
    id: 'users',
    label: 'Admin Users',
    icon: UserCog,
    path: '/users',
    permission: 'users.read',
  },
  {
    id: 'content',
    label: 'Content',
    icon: FileText,
    path: '/content',
    permission: 'content.read',
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    path: '/marketing',
    permission: 'marketing.read',
  },
  {
    id: 'affiliates',
    label: 'Affiliates',
    icon: UserPlus,
    path: '/affiliates',
    permission: 'affiliates.read',
  },
  {
    id: 'financial',
    label: 'Financial',
    icon: DollarSign,
    path: '/financial',
    permission: 'financial.read',
    disabled: true,
    comingSoon: true,
  },
  {
    id: 'inventory',
    label: 'Inventory',
    icon: Warehouse,
    path: '/inventory',
    permission: 'inventory.read',
    disabled: true,
    comingSoon: true,
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    path: '/analytics',
    permission: 'analytics.read',
    disabled: true,
    comingSoon: true,
  },
  {
    id: 'rushd-analytics',
    label: 'Rush\'d Analytics',
    icon: BarChart3,
    path: '/rushd/analytics',
    permission: 'rushd.read',
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    path: '/settings',
    permission: 'settings.read',
  },
];

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ isCollapsed, onToggle }: SidebarProps) {
  const location = useLocation();
  const { hasPermission } = useAuth();

  const filteredItems = sidebarItems.filter(item => 
    !item.permission || hasPermission(item.permission)
  );

  return (
    <motion.div
      initial={false}
      animate={{ width: isCollapsed ? 80 : 280 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="bg-white border-r border-gray-200 shadow-sm flex flex-col h-screen fixed left-0 top-0 z-40"
    >
      {/* Header */}
      <div className="p-3">
        <div className="relative">
          <AnimatePresence mode="wait">
            {!isCollapsed && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="flex items-center justify-center"
              >
                <div className="w-24 h-24 flex items-center justify-center">
                  <img
                    src="/lovable-uploads/bf2869c6-c7dd-4a46-a046-7189f9435aa3.png"
                    alt="JamPack'd Logo"
                    className="w-24 h-24 object-contain"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          <button
            onClick={onToggle}
            className={cn(
              "p-2 rounded-lg hover:bg-gray-100 transition-colors",
              isCollapsed 
                ? "w-full flex justify-center" 
                : "absolute top-0 right-0"
            )}
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4 text-gray-600" />
            ) : (
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            )}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {filteredItems.map((item) => {
          const isActive = location.pathname === item.path || 
            (item.path !== '/' && location.pathname.startsWith(item.path));
          
          return (
            <motion.div
              key={item.id}
              whileHover={!item.disabled ? { x: 2 } : {}}
              transition={{ duration: 0.2 }}
            >
              {item.disabled ? (
                <div
                  className={cn(
                    'sidebar-item',
                    'opacity-50 cursor-not-allowed',
                    'hover:bg-gray-50'
                  )}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0 text-gray-400" />
                  <AnimatePresence mode="wait">
                    {!isCollapsed && (
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.2 }}
                        className="ml-3 flex items-center justify-between flex-1"
                      >
                        <span className="font-medium text-sm text-gray-400">
                          {item.label}
                        </span>
                        {item.comingSoon && (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                              Coming Soon
                            </span>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>

                </div>
              ) : (
              <Link
                to={item.path}
                className={cn(
                  'sidebar-item',
                  isActive && 'sidebar-item-active'
                )}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                <AnimatePresence mode="wait">
                  {!isCollapsed && (
                    <motion.span
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2 }}
                      className="ml-3 font-medium text-sm"
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>
              </Link>
              )}
            </motion.div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="flex-shrink-0 p-3 border-t border-gray-200">
        <AnimatePresence mode="wait">
          {!isCollapsed && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
              className="text-xs text-gray-500 text-center space-y-2"
            >
              <div>
                <p>© {new Date().getFullYear()} JamPack'd</p>
                <p>Admin Portal v1.0.0</p>
              </div>
              <Attribution className="justify-center text-xs" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
} 
